import { FormattedMessage, useIntl } from 'react-intl';
import Button from '../core/Button';
import { z } from 'zod';
import { useCallback, useMemo, useState } from 'react';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSection } from '../FormSection';
import { FormElement } from '../FormElement';
import Input from '../core/Input';
import { InputCheckBox } from '../Inputs/InputCheckBox';
import { toast } from '@/hooks/useToast';
import {
  useApproveOrganizationApplication,
  useRejectOrganizationApplication,
  OrganizationApplicationDTO,
  OrganizationApplicationApproveDTO,
  useGetTenant,
  getListOrganizationApplicationsQueryKey,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { InputText } from '../Inputs/InputText';
import CheckBox from '../core/Checkbox';
import { trimData } from '@/helpers/trimData';
import { DialogWindow } from '../Dialog';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import {
  type PossibleDetails,
  type PickRequiredFields,
  payerDetailsTranslations,
  validatePayerDetails,
} from '@/helpers/payerDetails';

interface ApplicationHandleFormProps {
  application?: OrganizationApplicationDTO;
  hasDuplicateOrganization: boolean;
}

export const ApplicationHandleForm = ({
  application,
  hasDuplicateOrganization,
}: ApplicationHandleFormProps) => {
  const customErrorMap = useCustomErrorMap;
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const approveApplication = useApproveOrganizationApplication();
  const rejectApplication = useRejectOrganizationApplication();
  const { data: tenant } = useGetTenant();

  const [termsAgreed, setTermsAgreed] = useState<boolean>(false);
  const [rejectOpen, setRejectOpen] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [isInternal, setIsInternal] = useState<boolean>(false);
  const [formValues, setFormValues] =
    useState<OrganizationApplicationApproveDTO>({
      credit: -1,
      is_internal: false,
      enabled_collection_types: [],
      payer_details: {
        cost_center: application?.suggested_cost_center || '',
      },
    });

  const requiredProps = isInternal
    ? tenant?.required_internal_invoicing_details
    : tenant?.required_external_invoicing_details;

  const optionalProps = isInternal
    ? tenant?.optional_internal_invoicing_details
    : tenant?.optional_external_invoicing_details;

  const allProps = useMemo(
    () => [...(requiredProps || []), ...(optionalProps || [])],
    [requiredProps, optionalProps],
  );

  type RequiredDetails = NonNullable<typeof requiredProps>[number];
  type RequiredDetailsForm = OrganizationApplicationApproveDTO & {
    payer_details: PickRequiredFields<PossibleDetails, RequiredDetails>;
  };

  const schema = z.custom<RequiredDetailsForm>().superRefine((data, ctx) => {
    validatePayerDetails({
      requiredProps,
      optionalProps,
      details: data.payer_details,
      formatMessage,
      fieldPath: 'payer_details',
      ctx,
    });
  });

  const methods = useForm<OrganizationApplicationApproveDTO>({
    defaultValues: formValues,
    resolver: zodResolver(schema, {
      errorMap: customErrorMap(),
    }),
    mode: 'onBlur',
  });

  const [creditString, setCreditString] = useState('');

  const hasNoCreditLimit = methods.watch('credit') === -1;

  const creditError = useMemo(() => {
    if (!creditString) {
      return '';
    }
    if (isNaN(parseInt(creditString))) {
      return formatMessage({ defaultMessage: 'Ogiltigt värde' });
    }
    return methods.formState.errors.credit?.message;
  }, [creditString, formatMessage, methods.formState.errors.credit?.message]);

  const handleIsInternal = useCallback(() => {
    const internalValue = !methods.getValues('is_internal');
    methods.setValue('is_internal', internalValue);
    methods.reset({
      ...methods.getValues(),
      payer_details: {},
    });
    setIsInternal(!isInternal);
  }, [isInternal, methods]);

  if (!application) return;

  const handleRejectApplication = () => {
    if (approveApplication.isPending || rejectApplication.isPending) return;

    rejectApplication.mutate(
      {
        organizationApplicationID: application.id,
        data: {
          reason: rejectReason.trim(),
        },
      },
      {
        onSuccess: async () => {
          toast.success(
            {
              title: formatMessage(
                {
                  defaultMessage:
                    'Ansökan för {organizationName} med organisationsnummer {organizationNumber} avslogs',
                },
                {
                  organizationNumber: application.organization_number,
                  organizationName: application.name,
                },
              ),
            },
            { autoClose: 3000 },
          );
          await queryClient.invalidateQueries({
            queryKey: getListOrganizationApplicationsQueryKey(),
          });
          await navigate({
            to: '/$tenantId/organizations/$view',
            params: {
              tenantId: tenant!.id,
              view: 'applications',
            },
          });
        },
        onError: (error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: 'Ett fel inträffade',
              description:
                'Försök spara igen senare, eller vänligen kontakta support.',
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  };

  const handleApproveApplication = (
    data: OrganizationApplicationApproveDTO,
  ) => {
    if (approveApplication.isPending || rejectApplication.isPending) return;

    if (data.credit && data.credit < 0) {
      delete data.credit;
    }

    const onlyRelevantDetails = Object.fromEntries(
      Object.entries(data.payer_details).filter(([key]) =>
        allProps.includes(key as RequiredDetails),
      ),
    );

    approveApplication.mutate(
      {
        organizationApplicationID: application.id,
        data: trimData({ ...data, payer_details: onlyRelevantDetails }),
      },
      {
        onSuccess: async () => {
          toast.success(
            {
              title: formatMessage(
                {
                  defaultMessage:
                    'Ansökan för {organizationName} med organisationsnummer {organizationNumber} godkändes',
                },
                {
                  organizationNumber: application.organization_number,
                  organizationName: application.name,
                },
              ),
            },
            { autoClose: 3000 },
          );
          setFormValues(data);
          await queryClient.invalidateQueries({
            queryKey: getListOrganizationApplicationsQueryKey(),
          });
          await navigate({
            to: '/$tenantId/organizations/$view',
            params: {
              tenantId: tenant!.id,
              view: 'applications',
            },
          });
        },
        onError: (error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: 'Ett fel inträffade',
              description:
                'Försök spara igen senare, eller vänligen kontakta support.',
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        className="flex w-full flex-col gap-4 sm:w-panel-full"
        onSubmit={methods.handleSubmit(handleApproveApplication)}
      >
        <FormSection
          title={<FormattedMessage defaultMessage="Hantera" />}
          editMode={false}
          canSeeButton={checkUserPermissions({ permission: 'tenant:editor' })}
        >
          <div className="flex flex-col gap-2">
            <FormElement
              label={<FormattedMessage defaultMessage="Typ" />}
              input={
                <InputCheckBox
                  label="Intern organisation"
                  name="is_internal"
                  editMode={true}
                  onChange={handleIsInternal}
                />
              }
            />
            <FormElement
              label={<FormattedMessage defaultMessage="Kredit (SEK)" />}
              input={
                <div className="flex w-full items-center justify-end gap-5">
                  {!hasNoCreditLimit && (
                    <Input
                      name="credit"
                      className="max-w-32"
                      value={creditString}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value !== '0') {
                          value = value.replace(/^0+/, '');
                        }
                        setCreditString(value);
                        const creditValue = parseInt(value);
                        if (!isNaN(creditValue)) {
                          methods.setValue(
                            'credit',
                            creditValue >= 0 ? creditValue : 0,
                          );
                          methods.trigger();
                        }
                      }}
                      placeholder="0 SEK"
                      error={creditError}
                      onBlur={() =>
                        setCreditString(
                          methods.getValues('credit')?.toString() || '',
                        )
                      }
                    />
                  )}

                  <CheckBox
                    id="has_no_credit_limit"
                    checked={hasNoCreditLimit}
                    onClick={() => {
                      if (hasNoCreditLimit) {
                        methods.setValue('credit', 0, {
                          shouldDirty: true,
                        });
                      } else {
                        methods.setValue('credit', -1, {
                          shouldDirty: true,
                        });
                      }
                      methods.trigger();
                    }}
                    label={formatMessage({
                      defaultMessage: 'Ingen kreditgräns',
                    })}
                  />
                </div>
              }
            />
            <h4 className="text-lg">
              <FormattedMessage defaultMessage="Faktureringsdetaljer" />
            </h4>
            <div className="mb-4 flex flex-col gap-2 border-b pb-4">
              {requiredProps?.map((field) => (
                <FormElement
                  key={field}
                  id={`payer_details.${field}`}
                  label={
                    payerDetailsTranslations[
                      field as keyof typeof payerDetailsTranslations
                    ]
                  }
                  input={
                    <InputText
                      name={`payer_details.${field}`}
                      control={methods.control}
                      editMode={true}
                      error={methods.formState.errors.payer_details?.[field]}
                      helperTextReserveSpace
                      type="number"
                    />
                  }
                />
              ))}
              {optionalProps && optionalProps.length > 0 && (
                <p className="text-text-800">
                  <FormattedMessage defaultMessage="Valfria faktureringsdetaljer:" />
                </p>
              )}
              {optionalProps?.map((field) => (
                <FormElement
                  key={field}
                  id={`payer_details.${field}`}
                  label={
                    payerDetailsTranslations[
                      field as keyof typeof payerDetailsTranslations
                    ]
                  }
                  input={
                    <InputText
                      name={`payer_details.${field}`}
                      control={methods.control}
                      editMode={true}
                      error={methods.formState.errors.payer_details?.[field]}
                      helperTextReserveSpace
                      type="number"
                    />
                  }
                />
              ))}
            </div>
            <CheckBox
              className="mb-4"
              label="Jag har kontrollerat att uppgifterna i ansökan är korrekta och att organisationen ska få tillgång till tjänsten."
              id="terms_agreed"
              onClick={() => setTermsAgreed(!termsAgreed)}
            />

            {hasDuplicateOrganization ? (
              <div className="mb-4 rounded-lg border border-red-500 bg-red-50 p-4">
                <p className="font-bold text-text-950">
                  <FormattedMessage defaultMessage="Kan inte godkänna ansökan" />
                </p>
                <p className="mt-1 text-text-900">
                  <FormattedMessage defaultMessage="Det finns redan en organisation med samma namn eller organisationsnummer. Se varningen ovan för detaljer." />
                </p>
              </div>
            ) : (
              <Button
                type="submit"
                className="w-full"
                variant="filled"
                disabled={
                  !methods.formState.isValid ||
                  !termsAgreed ||
                  hasDuplicateOrganization
                }
                loading={approveApplication.isPending}
              >
                <FormattedMessage defaultMessage="Godkänn ansökan" />
              </Button>
            )}
          </div>
        </FormSection>
        {application.status !== 'rejected' && (
          <Button
            variant="ghost"
            color="red"
            className="w-full"
            onClick={() => setRejectOpen(true)}
          >
            <FormattedMessage defaultMessage="Avslå ansökan" />
          </Button>
        )}
      </form>
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Avslå ansökan',
        })}
        content={
          <div className="flex flex-col gap-2">
            <p>
              <FormattedMessage defaultMessage="Anledning till att ansökan avslås (skickas till den som ansökte)." />
            </p>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <Button
              variant="filled"
              color="red"
              className="mt-8 w-full"
              loading={rejectApplication.isPending}
              onClick={handleRejectApplication}
            >
              <FormattedMessage defaultMessage="Avslå ansökan" />
            </Button>
          </div>
        }
        onOpenChange={setRejectOpen}
        open={rejectOpen}
      />
    </FormProvider>
  );
};
